import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { Lightbulb, Target, Users, Zap, Clock, TrendingUp, MapPin } from 'lucide-react';

const BASE_WIDTH = 480 * 1.5;
const WIDTH_STEP = 0.18;

const pyramidContent = [
  {
    id: 'foundation',
    title: 'Foundation',
    subtitle: 'Products, Systems, & Strategy at Scale',
    description: (
      <>
        My foundation was built at <b>Accenture</b>, where I led <b>15+ enterprise transformation projects</b> across automotive, supply chain, utilities, sustainable energy, & consumer technology. I specialized in <b>business-process design</b>, <b>GTM strategy</b>, & <b>value realization</b>, guiding clients through digitization efforts that reshaped operations & customer experience. I defined, built, & deployed <b>RPA workflows, chatbots, & web/mobile self-service enhancements</b> that added $12M per year in operating value. I also created <b>visual analytics & executive reporting</b> that turned complex data into actionable insights & strategic plans. Beyond client work, I led <b>Corporate Citizenship</b>, mobilizing 200+ volunteers & $50,000+ each year while providing pro-bono consulting that strengthened the local community.
      </>
    ),
    bullets: [
      <b>Led 15+ cross-industry transformation engagements</b>,
      <b>Business-process design, GTM planning, & value realization</b>,
      <b>RPA, chatbot, & web/mobile self-service solutions adding $12M annually</b>,
      <b>Visual analytics & executive reports for leadership decisions</b>,
      <b>Mobilized 200+ volunteers & $50,000+ annually through pro-bono community work</b>,
    ],
    color: 'bg-blue-200',
  },
  {
    id: 'launchpad',
    title: 'Launchpad',
    subtitle: 'Innovating with OEMs, Shaping the Future of Mobility',
    description: (
      <>
        At <b>Volvo Cars & Ford</b>, I moved into <b>product leadership & corporate strategy</b>. At Volvo, I led the strategic development of the <b>AAOS app platform</b>, negotiated partner models, & co-designed in-cabin UX innovations. I also created a <b>loyalty program</b> that increased EV-charging adoption & customer retention. At Ford, I drove <b>competitive analysis</b>, quantified <b>$1B+ in software value streams</b>, & presented board proposals covering EV, AV, & digital services, turning long-range vision into product direction that shareholders value & customers can resonate with.
      </>
    ),
    bullets: [
      <b>Led AAOS roadmap, onboarding 10+ third-party apps in 6 months</b>,
      <b>Negotiated software & hardware partner deals for Volvo</b>,
      <b>Designed loyalty program boosting EV-charging adoption & retention</b>,
      <b>Quantified $1B+ in software value streams for Ford board proposals</b>,
      <b>Built EV & AV business cases presented to senior leadership</b>,
    ],
    color: 'bg-green-200',
  },
  {
    id: 'inflection',
    title: 'Inflection Point',
    subtitle: 'Scaling FreightTech with AI & GTM Execution',
    description: (
      <>
        At <b>Einride & CloudTrucks</b>, I lead initiatives where <b>logistics, AI, & product strategy converge</b>. At Einride, I defined <b>EV-freight SaaS offerings</b>, partnered with governments & utilities, & closed <b>$1M+ in annual recurring revenue</b> through hardware & transportation SaaS. At CloudTrucks, I am the <b>first Partnerships hire</b>, lead the <b>$50M Truck Ownership program</b>, structure sublease agreements with independent drivers, & build an <b>API-enabled partner ecosystem</b> across load booking, fuel, maintenance, & leasing. I align Legal, Finance, & Product on <b>GTM metrics that move R&D initiatives forward</b>.
      </>
    ),
    bullets: [
      <b>Defined EV-freight SaaS products generating $1M+ ARR</b>,
      <b>First Partnerships hire at CloudTrucks, leading $50M Truck Ownership program</b>,
      <b>Structured driver sublease contracts & risk-mitigated terms</b>,
      <b>Secured enterprise API integrations for load booking, fuel, maintenance, & leasing</b>,
      <b>Unified Legal, Finance, & Product on data-driven GTM metrics</b>,
    ],
    color: 'bg-yellow-200',
  },
  {
    id: 'next-horizon',
    title: 'Next Horizon',
    subtitle: 'What I Am Building Toward',
    description: (
      <>
        I now aim to bring my <b>11+ years of layered experience</b> into a role where I can lead from the center, whether as Chief of Staff, Head of Strategy & Partnerships, or Product Leader. I thrive in fast-moving environments where <b>AI, mobility, & digital ecosystems</b> demand both <b>strategic clarity & hands-on execution</b>. My career has focused on <b>building societal necessities</b>, aligning teams around complex goals, & converting strategy into momentum. I am ready to do the same for my next organization.
      </>
    ),
    bullets: [
      <b>Architect founder-office operating rhythm & executive decision cadence</b>,
      <b>Drive AI-powered value creation & go-to-market acceleration</b>,
      <b>Build new functions, partnerships, & products from zero to scale</b>,
      <b>Advise C-suite & boards on growth, value architecture, & ecosystem design</b>,
      <b>Champion innovation that benefits people, planet, & profit</b>,
    ],
    color: 'bg-purple-200',
  },
];

function Pyramid() {
  const [expanded, setExpanded] = useState(null);
  const [clickedLayer, setClickedLayer] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const layerRefs = useRef([]);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLayerClick = (index) => {
    const newClickedLayer = clickedLayer === index ? null : index;
    setClickedLayer(newClickedLayer);
    setExpanded(newClickedLayer);
    setTimeout(() => {
      if (layerRefs.current[index] && clickedLayer !== index) {
        const yOffset = -80;
        const elementTop = layerRefs.current[index].getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: elementTop, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleMouseEnter = (index) => {
    if (clickedLayer === null) {
      setExpanded(index);
    }
  };

  const handleMouseLeave = () => {
    if (clickedLayer === null) {
      setExpanded(null);
    }
  };

  // Calculate responsive width for mobile pyramid
  const getMobileWidth = (index) => {
    const screenWidth = window.innerWidth;
    const padding = 32; // 16px on each side
    const availableWidth = screenWidth - padding;
    
    // Linear 15% reduction per tier
    const widthPercent = 1 - (index * 0.15);
    return Math.max(availableWidth * widthPercent, 200); // Minimum 200px
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-primary-50 to-white py-12 px-4 pt-20">
      <h1 className="text-4xl font-bold mb-2 text-center text-primary-900">Neil's Journey to the Apex</h1>
      <p className="mb-8 text-primary-700 text-center max-w-xl">Click or hover on each layer to learn more.</p>
      <div className="relative w-full flex flex-col items-center" style={{ maxWidth: isMobile ? '100%' : BASE_WIDTH }}>
        {pyramidContent.map((layer, idx) => {
          let width;
          if (isMobile) {
            // Mobile: use responsive pyramid width
            width = expanded === idx ? '100%' : getMobileWidth(idx);
          } else {
            // Desktop: use original logic
            const baseWidth = BASE_WIDTH;
            const widthPercent = 1 - idx * WIDTH_STEP;
            width = baseWidth * widthPercent;
            if (expanded === idx && idx > 0) {
              width = baseWidth * (1 - (idx - 1) * WIDTH_STEP);
            }
            if (idx === 0) width = baseWidth;
          }

          return (
            <div
              key={layer.id}
              ref={el => layerRefs.current[idx] = el}
              className={`cursor-pointer transition-all duration-300 ${layer.color} shadow-lg border border-gray-200 mb-2 flex flex-col items-center justify-center ${expanded === idx ? 'z-20 scale-105' : 'z-10'} `}
              style={{
                width: width,
                minHeight: expanded === idx ? 180 : 80,
                borderRadius: '24px',
                marginTop: idx === 0 ? 0 : (expanded !== null && expanded !== idx ? -8 : -24),
              }}
              onClick={() => handleLayerClick(idx)}
              onMouseEnter={() => handleMouseEnter(idx)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="py-4 px-6 w-full text-center flex flex-col items-center">
                <div className="flex items-center justify-center gap-2">
                  <h3 className="text-xl font-semibold">{layer.title}</h3>
                  <ChevronDown size={20} className={`transition-transform ${expanded === idx ? 'rotate-180' : ''}`} />
                </div>
                <p className="text-sm italic text-gray-700 mb-2">{layer.subtitle}</p>
                {(expanded === idx) && (
                  <>
                    <div className="text-gray-800 mb-2 whitespace-pre-line text-base text-left max-w-2xl mx-auto">{layer.description}</div>
                    <ul className="list-disc list-inside text-left mx-auto max-w-md">
                      {layer.bullets.map((b, i) => (
                        <li key={i} className="mb-1">{b}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const About = () => {
  const leadershipPrinciples = [
    {
      icon: Clock,
      title: 'Long-Term Strategic Thinking',
      description: 'I influence individuals and companies to make short-term decisions that benefit the long now, ensuring sustainable value creation beyond quarterly cycles.'
    },
    {
      icon: Target,
      title: 'Strategic Clarity',
      description: 'Transform complex challenges into clear, executable strategies that align stakeholders and drive measurable outcomes across time horizons.'
    },
    {
      icon: Users,
      title: 'Cross-Functional Leadership',
      description: 'Bridge silos and build consensus across diverse teams to achieve organizational objectives through collaborative execution.'
    },
    {
      icon: Zap,
      title: 'AI-Enabled Transformation',
      description: 'Leverage emerging technologies to create sustainable competitive advantages and operational excellence for the future.'
    }
  ];

  const strategicFocusAreas = [
    {
      icon: Target,
      title: 'Chief of Staff',
      description: 'Strategic advisor and operational partner to C-Suite executives',
      color: 'from-blue-100 to-blue-200',
      iconColor: 'text-blue-700'
    },
    {
      icon: Users,
      title: 'Partnerships',
      description: 'Develop and execute strategic partnership ecosystems',
      color: 'from-primary-100 to-accent-100',
      iconColor: 'text-primary-700'
    },
    {
      icon: TrendingUp,
      title: 'Corporate Strategy',
      description: 'Shape organizational direction and market positioning',
      color: 'from-green-100 to-green-200',
      iconColor: 'text-green-700'
    },
    {
      icon: Lightbulb,
      title: 'Product Strategy',
      description: 'Drive product vision and market-driven innovation',
      color: 'from-purple-100 to-purple-200',
      iconColor: 'text-purple-700'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Neil's Journey to the Apex */}
      <Pyramid />
      
      {/* Leadership Philosophy */}
      <section className="section bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="section-title">Leadership Philosophy</h1>
            <p className="section-subtitle">
              Driving organizational transformation through strategic vision and operational excellence
            </p>
          </motion.div>
          
          <div className="space-y-8 text-primary-700 text-lg leading-relaxed">
            <div className="bg-primary-50 rounded-xl p-6 border border-primary-100">
              <p className="text-primary-900 font-semibold text-xl mb-4 text-center">
                <span className="italic font-bold">I believe in influencing individuals and organizations to make short-term decisions that benefit the long now.</span>
              </p>
            </div>
            
            <p>
              My approach centers on strategic clarity, disciplined execution, and human-centered innovation. With extensive experience across Accenture consulting, automotive R&D, and freight-tech startups, I've consistently delivered results by translating C-suite vision into clear, actionable strategies.
            </p>
            <p>
              I've reshaped corporate strategies, scaled AI-driven solutions, and launched transformative partnerships, always prioritizing sustainable growth over short-term gains. I'm driven by creating measurable impacts that compound over time, enabling organizations to achieve lasting competitive advantage.
            </p>
            <p>
              My cross-sector experience spans consulting (Accenture), automotive innovation (Volvo Cars), and freight-tech scale-ups (CloudTrucks), equipping me to lead strategic initiatives across diverse industries and business models.
            </p>
          </div>
        </div>
       
        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="flex justify-center items-center py-10"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-primary-400 rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Strategic Focus */}
      <section className="section bg-primary-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="section-title">Strategic Focus Areas</h2>
            <p className="section-subtitle">
              Current professional focus and areas of strategic leadership
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {strategicFocusAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 border border-primary-100 text-center group hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${area.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <area.icon size={32} className={area.iconColor} />
                </div>
                <h3 className="text-lg font-semibold text-primary-900 mb-3">
                  {area.title}
                </h3>
                <p className="text-primary-800 text-sm">
                  {area.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 